import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ChevronDown } from 'lucide-react';
import { ADMIN_NAV, DASHBOARD_LINK } from '@/components/admin/adminNav';
import useInboundEmailUnread from '@/hooks/useInboundEmailUnread';
import useCaseCounts from '@/hooks/useCaseCounts';
import useSupportBadges from '@/hooks/useSupportBadges';

// Persistent left sidebar for the admin area. Always visible on lg+, slides in
// as a drawer on mobile (toggled from the AdminLayout top bar).
export default function AdminSidebar({ open, onClose }) {
  const { pathname } = useLocation();
  const emailUnread = useInboundEmailUnread();
  const caseCounts = useCaseCounts();
  const supportBadges = useSupportBadges();
  // Which tier is open. `undefined` means "follow the page you're on", so the
  // tier holding the current page opens by itself; once an admin clicks a
  // header their choice stands until the next navigation.
  const [openGroup, setOpenGroup] = useState(undefined);

  // A path belongs to the most specific nav entry that claims it. An entry can
  // also claim extra routes (`match`) — the sibling page behind a shared tab
  // strip, or the tabs folded into Customer CRM — so the consolidated link
  // stays lit on every page it now stands for.
  const allPaths = [
    DASHBOARD_LINK.to,
    ...ADMIN_NAV.flatMap((s) => s.items.flatMap((i) => [i.to, ...(i.match || [])])),
  ];
  const isActive = (to, match = []) => {
    const paths = [to, ...match];
    if (paths.some((p) => pathname === p)) return true;
    if (to === '/admin/dashboard') return false;
    const owner = paths.find((p) => pathname.startsWith(p + '/'));
    if (!owner) return false;
    return !allPaths.some(
      (p) => !paths.includes(p) && p.length > owner.length && (pathname === p || pathname.startsWith(p + '/'))
    );
  };

  // The tier that owns the page being viewed, so it opens on load and whenever
  // the admin navigates.
  const activeSection = ADMIN_NAV.find((section) =>
    section.items.some((item) => isActive(item.to, item.match))
  );

  useEffect(() => {
    setOpenGroup(undefined);
  }, [pathname]);

  const isGroupOpen = (section) =>
    openGroup === undefined ? activeSection?.group === section.group : openGroup === section.group;

  // One tier at a time, so the menu always fits the viewport.
  const toggleGroup = (section) => setOpenGroup(isGroupOpen(section) ? null : section.group);

  const badgeCount = (item) => {
    switch (item.badge) {
      case 'cases':
        return caseCounts.open;
      case 'email':
        return emailUnread;
      case 'rma':
        return supportBadges.rma;
      case 'reviews':
        return supportBadges.reviews;
      default:
        return 0;
    }
  };

  const itemClass = (active) =>
    `flex items-center gap-2.5 px-2.5 py-2 text-xs font-medium rounded-sm border-l-2 transition-colors ${
      active
        ? 'bg-primary/15 text-primary border-primary'
        : 'text-white/55 hover:bg-white/5 hover:text-white border-transparent'
    }`;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-[#0a0a0c] border-r border-white/5 z-50 flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5 shrink-0">
          <Link to="/admin/dashboard" onClick={onClose} className="flex items-center gap-2.5">
            <span className="w-7 h-7 bg-primary flex items-center justify-center">
              <DASHBOARD_LINK.icon className="w-4 h-4 text-white" />
            </span>
            <span className="text-white font-black text-sm tracking-wider">ADMIN</span>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden text-white/40 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          <div>
            <Link
              to={DASHBOARD_LINK.to}
              onClick={onClose}
              className={`flex items-center gap-2.5 px-2.5 py-2 text-xs font-bold tracking-wider rounded-sm border-l-2 transition-colors ${
                isActive(DASHBOARD_LINK.to)
                  ? 'bg-primary/15 text-primary border-primary'
                  : 'text-white/60 hover:bg-white/5 hover:text-white border-transparent'
              }`}
            >
              <DASHBOARD_LINK.icon className="w-3.5 h-3.5 shrink-0" />
              {DASHBOARD_LINK.label.toUpperCase()}
            </Link>
          </div>

          {ADMIN_NAV.map((section) => {
            const groupOpen = isGroupOpen(section);
            // While a group is collapsed its outstanding work is surfaced on the
            // heading, so nothing waiting on staff can be hidden behind it.
            const collapsedBadge = section.items.reduce((sum, i) => sum + badgeCount(i), 0);

            return (
              <div key={section.group}>
                <button
                  onClick={() => toggleGroup(section)}
                  aria-expanded={groupOpen}
                  className={`w-full flex items-center gap-1.5 px-2 mb-1.5 transition-colors ${
                    groupOpen ? 'text-white/60' : 'text-white/25 hover:text-white/50'
                  }`}
                >
                  <ChevronDown
                    className={`w-3 h-3 shrink-0 transition-transform duration-200 ${
                      groupOpen ? 'rotate-180' : ''
                    }`}
                  />
                  <span className="text-[10px] font-bold tracking-widest">
                    {section.group.toUpperCase()}
                  </span>
                  {!groupOpen && collapsedBadge > 0 && (
                    <span className="ml-auto bg-primary text-white text-[9px] font-bold px-1.5 py-0.5">
                      {collapsedBadge}
                    </span>
                  )}
                </button>

                {groupOpen && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const count = badgeCount(item);
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={onClose}
                          className={itemClass(isActive(item.to, item.match))}
                        >
                          <Icon className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate flex-1">{item.label}</span>
                          {item.badge && count > 0 && (
                            <span
                              title={
                                item.badge === 'cases' && caseCounts.overdue > 0
                                  ? `${count} open · ${caseCounts.overdue} overdue`
                                  : undefined
                              }
                              className={`text-[9px] font-bold px-1.5 py-0.5 shrink-0 ${
                                item.badge === 'cases' && caseCounts.overdue > 0
                                  ? 'bg-red-500 text-white'
                                  : 'bg-primary text-white'
                              }`}
                            >
                              {count}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}